
import { CampaignData } from './types';
import { toast } from '@/hooks/use-toast';
import { createCampaign } from '@/services/smsApi';

// Helper function to check if any filters are active
export const hasActiveFilters = (filters: CampaignData['filters']): boolean => {
  // Check if any new filters are active (different from defaults)
  return (
    (filters.totalOrders && (filters.totalOrders.lowerLimit !== 1 || filters.totalOrders.upperLimit !== null)) ||
    (filters.lastVisitDate && (filters.lastVisitDate.fromDate !== null || filters.lastVisitDate.toDate !== null)) ||
    (filters.averageOrderValue && (filters.averageOrderValue.lowerLimit !== 0 || filters.averageOrderValue.upperLimit !== null)) ||
    (filters.totalSpend && (filters.totalSpend.lowerLimit !== 0 || filters.totalSpend.upperLimit !== null)) ||
    (filters.loyaltyTier && filters.loyaltyTier.length > 0)
  );
};

// Check if a campaign step is complete
export const isStepComplete = (step: string, campaignData: CampaignData): boolean => {
  switch (step) {
    case 'details':
      return campaignData.name.trim().length > 0;
    case 'audience':
      return campaignData.selectedRestaurants.length > 0;
    case 'message':
      return campaignData.message.trim().length > 0;
    case 'review':
      // Review step is only complete when all previous steps are complete
      return (
        campaignData.name.trim().length > 0 &&
        campaignData.selectedRestaurants.length > 0 &&
        campaignData.message.trim().length > 0
      );
    default:
      return false;
  }
};

// Check if a step can be navigated to (different from completion)
export const isStepNavigable = (step: string, campaignData: CampaignData): boolean => {
  switch (step) {
    case 'details':
    case 'audience':
    case 'message':
      // Steps 1-3 are always navigable
      return true;
    case 'review':
      // Review step is only navigable when all previous steps are complete
      return (
        campaignData.name.trim().length > 0 &&
        campaignData.selectedRestaurants.length > 0 &&
        campaignData.message.trim().length > 0
      );
    default:
      return false;
  }
};

// Helper to extract organisation ID from user profile
const extractOrganisationId = (userProfile: any): string | null => {
  if (!userProfile) return null;
  
  // Check various property naming conventions
  if (userProfile.organisation_id) return userProfile.organisation_id;
  if (userProfile.organisationId) return userProfile.organisationId;
  if (userProfile.organisation_id) return userProfile.organisation_id;
  if (userProfile.organisationId) return userProfile.organisationId;
  
  console.warn("Could not find organisation ID in user profile:", userProfile);
  return null;
};

// Build target criteria from campaign filters
const buildTargetCriteria = (campaignData: CampaignData): any => {
  const criteria: any = {};
  
  if (campaignData.audienceType === 'filtered') {
    // New filter formats
    if (campaignData.filters.totalOrders) {
      criteria.totalOrders = campaignData.filters.totalOrders;
    }
    
    if (campaignData.filters.lastVisitDate) {
      criteria.lastVisitDate = campaignData.filters.lastVisitDate;
    }
    
    if (campaignData.filters.averageOrderValue) {
      criteria.averageOrderValue = campaignData.filters.averageOrderValue;
    }
    
    if (campaignData.filters.totalSpend) {
      criteria.totalSpend = campaignData.filters.totalSpend;
    }
    
    // Keep loyalty tier as it's still being used
    if (campaignData.filters.loyaltyTier && campaignData.filters.loyaltyTier.length > 0) {
      criteria.loyaltyTier = campaignData.filters.loyaltyTier;
    }
  }
  
  return criteria;
};

// Format date preserving the user-selected date information
const formatScheduleTime = (date: Date | null, timeString: string): string => {
  if (!date) return 'now';
  
  // Extract year, month, and day from the user-selected date
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  // Format as YYYY-MM-DDThh:mm:00
  return `${year}-${month}-${day}T${timeString}:00`;
};

// Save campaign to Supabase and the API service 
export const saveCampaignToSupabase = async (
  campaignData: CampaignData & { audience_data?: any; audienceSize?: number }, 
  userProfile?: any
): Promise<boolean> => {
  try {
    console.log('Preparing campaign data for save:', campaignData);
    console.log('User profile for campaign save:', userProfile);
    
    if (!userProfile) {
      console.error('User profile is missing for campaign creation');
      throw new Error('User profile is required to save a campaign.');
    }
    
    if (!userProfile.restaurants || userProfile.restaurants.length === 0) {
      console.error('No restaurants found in user profile:', userProfile);
      throw new Error('No restaurant access found. Please select a restaurant.');
    }
    
    // Determine if this is an organisation-wide campaign
    const isOrgWide = campaignData.selectedRestaurants?.includes('all');
    console.log('Is organisation-wide campaign:', isOrgWide);
    
    // Get the organisation ID from the user profile
    const organisationId = extractOrganisationId(userProfile);
    console.log('Extracted organisation ID:', organisationId);
    
    if (isOrgWide && !organisationId) {
      console.error('No organisation ID available for org-wide campaign');
      throw new Error('Organisation ID is required for organisation-wide campaigns.');
    }
    
    // Create target criteria from filters
    const targetCriteria = buildTargetCriteria(campaignData);
    console.log('Built target criteria:', targetCriteria);
    
    // Format the campaign data for the API
    const formattedCampaignData = {
      // For org-wide campaigns, set restaurant_id to null
      restaurant_id: isOrgWide
                     ? null
                     : (campaignData.selectedRestaurants?.length === 1)
                       ? campaignData.selectedRestaurants[0]
                       : userProfile.restaurants[0].id,

      // Always include organisation_id when available (Fix #2)
      organisation_id: organisationId,

      campaign_name: campaignData.name,
      campaign_content: campaignData.message,
      description: campaignData.description,
      schedule_type: campaignData.scheduleType,
      schedule_time: campaignData.scheduleType === 'now' ? 'now' :
                    formatScheduleTime(campaignData.scheduleDate, campaignData.scheduleTime),
      audience_type: campaignData.audienceType,

      // For org-wide campaigns, set selected_restaurants to ["all"]
      selected_restaurants: isOrgWide
                           ? ["all"]
                           : campaignData.selectedRestaurants,

      audience_filters: campaignData.filters,
      target_criteria: targetCriteria,

      // Add audience_size field (Fix #1) - use the passed audienceSize or fallback to campaignData
      audience_size: campaignData.audienceSize || campaignData.audienceSize,

      // Add segments per message for proper unit calculation
      segments_per_message: campaignData.segmentsPerMessage || 1,

      // Add promo code ID if present (for ROI tracking)
      promo_code_id: campaignData.promo_code_id || null,
      attribution_window_days: campaignData.attribution_window_days || 7,

      // CRITICAL: Pass the audience data for filtered campaigns to enable proper backend routing
      audience_data: campaignData.audience_data
    };
    
    console.log('Formatted campaign data for API call:', {
      ...formattedCampaignData,
      // Log audience data details for debugging
      audience_data_length: formattedCampaignData.audience_data?.length || 0,
      has_audience_data: !!formattedCampaignData.audience_data,
      is_filtered: formattedCampaignData.audience_type === 'filtered'
    });
    
    // Create campaign via the API
    const result = await createCampaign(formattedCampaignData);
    console.log('Campaign created successfully, API response:', result);
    
    return true;
  } catch (error) {
    console.error('Error creating campaign:', error);
    throw error;
  }
};

// Export the original function name for backward compatibility
export { saveCampaignToSupabase as saveCampaignToApi };
